import type {
  ActivePaymentLinkParams,
  ActivePaymentLinkResponse,
  ActivePoliciesListParams,
  ActivePoliciesListResponse,
  OptionsListResponse,
  PaymentOptionsResponse,
  PolicyResponse,
  RenewQuoteParams,
  RenewQuoteResponse,
  TotalPriceParams,
  TotalPriceResponse,
  UpdatePolicyrenewalParams,
} from './RenewService.types';
// TODO: fix import from upper layer
import { serializeToFormData } from '@/app/plugins/http/http.utils';

import { BaseService, defineApi } from '@/shared/api';
import { getPolicyMockResponse } from './RenewService.mocks';
import {
  serializeActivePoliciesList,
  serializeOptionsList,
  serializePaymentOptions,
  serializeRenewQuote,
} from './RenewService.serialize';

class RenewService extends BaseService {
  // https://qicbizdev.atlassian.net/wiki/spaces/QB/pages/1142816803
  async fetchRenewQuote(params: RenewQuoteParams) {
    const formData = serializeToFormData(params, false);
    const data: RenewQuoteResponse = await this.post('/getrenewquote', formData);
    return serializeRenewQuote(data);
  }

  // https://qicbizdev.atlassian.net/wiki/spaces/QB/pages/1569587201
  async fetchNewRenewQuote(params: {
    policyNo?: string;
    transId?: string;
    locale: string;
    quoteGuid?: string;
  }) {
    const formData = serializeToFormData(params, false);
    const data: RenewQuoteResponse = await this.post('/calculaterenewquote', formData);
    return serializeRenewQuote(data);
  }

  // https://qicbizdev.atlassian.net/wiki/spaces/QB/pages/1091371020
  async fetchActivePaymentLinks(
    params: ActivePaymentLinkParams,
  ): Promise<ActivePaymentLinkResponse> {
    const formData = serializeToFormData(params, false);
    return await this.post('/getactivepaymentlinks', formData);
  }

  // https://qicbizdev.atlassian.net/wiki/spaces/QB/pages/216858625/
  async fetchPaymentOptions(params: { quoteno?: string; locale: string }) {
    const data = await this.get<Record<number, PaymentOptionsResponse>>('/getpaymentoptions', {
      params,
    });
    return serializePaymentOptions(data);
  }

  // https://qicbizdev.atlassian.net/wiki/spaces/QB/pages/876904468
  async fetchCSRFToken() {
    const response = await fetch(`${import.meta.env.VUE_APP_API_URL}/getcsrftoken`, {
      method: 'POST',
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`CSRF token fetch failed with status ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    return data['X-CSRF-Token'];
  }

  // https://qicbizdev.atlassian.net/wiki/spaces/QB/pages/1033797690
  async fetchActivePoliciesList(payload: ActivePoliciesListParams) {
    const data: ActivePoliciesListResponse[] = await this.get('/getpolicydetailsbyguid', {
      params: {
        guid: payload.guid,
        locale: payload.locale,
      },
      headers: {
        'X-CSRF-Token': payload.CSRFToken,
      },
    });
    return serializeActivePoliciesList(data);
  }

  // https://qicbizdev.atlassian.net/wiki/spaces/QB/pages/41287866
  async updatePolicyrenewal(params: UpdatePolicyrenewalParams) {
    const formData = serializeToFormData(params, false);
    const data: ActivePoliciesListResponse[] = await this.post(
      '/policyrenewalupdatemotor',
      formData,
    );
    return serializeActivePoliciesList(data);
  }

  // https://qicbizdev.atlassian.net/wiki/spaces/QB/pages/22904945
  async fetchTotalPrice({ params, signal }: { params: TotalPriceParams; signal?: AbortSignal }) {
    return await this.get<TotalPriceResponse>('/gettotalprice', { params, signal });
  }

  // TODO: вынести запрос за списком в другой сервис
  // https://qicbizdev.atlassian.net/wiki/spaces/QB/pages/22806678
  async fetchPlateTypes(params: { vehicleType?: string; vehicleUsage?: string }) {
    const data = await this.get<Record<number, OptionsListResponse>>('/getlists/platetypes', {
      params,
    });
    return serializeOptionsList(data);
  }

  // TODO: вынести запрос за списком в другой сервис
  // https://qicbizdev.atlassian.net/wiki/spaces/QB/pages/22806678
  async fetchVehicleTypes() {
    const data = await this.get<Record<number, OptionsListResponse>>('/getlists/bodytypes');
    return serializeOptionsList(data);
  }

  // TODO: вынести запрос за списком в другой сервис
  // https://qicbizdev.atlassian.net/wiki/spaces/QB/pages/22806678
  async fetchVehicleShapes(params: { vehicleType?: string }) {
    const data = await this.get<Record<number, OptionsListResponse>>('/getlists/vehicleshapes', {
      params,
    });
    return serializeOptionsList(data);
  }

  // https://qicbizdev.atlassian.net/wiki/spaces/QB/pages/22937651
  async fetchPolice(): Promise<PolicyResponse> {
    if (import.meta.env.MODE === 'development') return Promise.resolve(getPolicyMockResponse);
    return await this.get('/getpolicy');
  }
}

export const useRenewService = defineApi(RenewService);
